import React, { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Home, KeySquare, Radio, BarChart3, UserRoundCog } from "lucide-react";

import { PLATFORMS } from "../../constants/platforms";
import getVisiblePlatforms from "../utils/getVisiblePlatforms";

/**
 * Основной сайдбар:
 * — выезжает из-под фиксированного хедера (top: 70px)
 * — без верхней «шапочки»
 * — верхний внутренний отступ 75px
 * — активные пункты подсвечиваются (как на старой версии)
 */
const Sidebar = ({
  platform,
  onPlatformSwitch,
  subscription,
  isAdmin,
  visible,
  onClose,
}) => {
  const ref = useRef(null);
  const visiblePlatforms = getVisiblePlatforms(isAdmin);

  // Accent по платформе
  const accent = platform === "kick"
    ? { bar: "bg-emerald-400", glow: "shadow-[0_0_8px_rgba(16,185,129,.8)]" }
    : platform === "twitch"
    ? { bar: "bg-violet-400",  glow: "shadow-[0_0_8px_rgba(167,139,250,.9)]" }
    : { bar: "bg-cyan-400",    glow: "shadow-[0_0_8px_rgba(34,211,238,.9)]" };

  useEffect(() => {
    document.body.classList.remove(...PLATFORMS.map((p) => `${p.id}-theme`));
    document.body.classList.add(`${platform}-theme`);
  }, [platform]);

  // click-outside (кроме .toggle-btn)
  useEffect(() => {
    if (!visible) return;
    const h = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (e.target.closest(".toggle-btn")) return;
      onClose?.();
    };
    const esc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("pointerdown", h, true);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", h, true);
      document.removeEventListener("keydown", esc);
    };
  }, [visible, onClose]);

  const platformAllowed =
    isAdmin || !PLATFORMS.find((p) => p.id === platform)?.adminOnly;

  const handlePlatformSwitch = (id) => {
    if (!isAdmin && PLATFORMS.find((p) => p.id === id)?.adminOnly) return;
    onPlatformSwitch?.(id);
    onClose?.();
  };

  // Рендер одного пункта меню с активной подсветкой
  const NavItem = ({ to, icon: Icon, label, end = false }) => (
    <NavLink to={to} end={end} className="block" onClick={() => onClose?.()}>
      {({ isActive }) => (
        <div
          className={`relative flex items-center gap-3 rounded-xl px-4 py-2 transition
            ring-1
            ${isActive
              ? "bg-white/15 text-white ring-white/25 shadow"
              : "bg-white/7.5 text-slate-300 hover:text-white hover:bg-white/10 ring-white/10"}`}
          style={{ backdropFilter: "blur(6px)" }}
        >
          {isActive && (
            <span
              className={`absolute left-1 top-1/2 -translate-y-1/2 h-2/3 w-[3px] rounded-full ${accent.bar} ${accent.glow}`}
            />
          )}
          <Icon className="h-4 w-4" />
          <span className="font-semibold">{label}</span>
        </div>
      )}
    </NavLink>
  );

  return (
    <aside
      ref={ref}
      className={`fixed left-0 z-50 w-72 backdrop-blur-xl
                 bg-slate-900/70 border-r border-white/10 shadow-2xl
                 transition-transform duration-300
                 ${visible ? "translate-x-0" : "-translate-x-full"}`}
      style={{ top: "70px", height: "calc(100vh - 70px)" }}
      aria-hidden={!visible}
    >
      {/* Внутренний контейнер: верх 75px */}
      <div
        className="relative"
        style={{ paddingTop: 50, paddingRight: 20, paddingLeft: 20, paddingBottom: 16 }}
      >
        {/* Переключатель платформ (без слова «Платформы») */}
        <div className="mb-6">
          <div className="mt-0 flex flex-wrap gap-2">
            {visiblePlatforms.map((p) => {
              const active = platform === p.id;
              const a =
                p.id === "kick"
                  ? { bar: "bg-emerald-400", glow: "shadow-[0_0_8px_rgba(16,185,129,.8)]" }
                  : p.id === "twitch"
                  ? { bar: "bg-violet-400", glow: "shadow-[0_0_8px_rgba(167,139,250,.9)]" }
                  : { bar: "bg-cyan-400", glow: "shadow-[0_0_8px_rgba(34,211,238,.9)]" };

              return (
                <button
                  key={p.id}
                  onClick={() => handlePlatformSwitch(p.id)}
                  className={`relative px-3 py-1.5 rounded-lg text-sm transition
                    ring-1
                    ${active
                      ? "bg-white/15 text-white ring-white/25"
                      : "text-slate-300 hover:text-white hover:bg-white/10 ring-white/10"}`}
                  title={p.label}
                  style={{ backdropFilter: "blur(6px)" }}
                >
                  {p.label}
                  {active && (
                    <span
                      className={`pointer-events-none absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-10 h-[2px] rounded-full ${a.bar} ${a.glow}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Навигация */}
        <nav className="space-y-3">
          <NavItem to="/feed" icon={Home} label="Главная страница" end />
          {platformAllowed && (
            <>
              <NavItem to={`/${platform}/oauth`} icon={KeySquare} label="Токены" />
              <NavItem to={`/${platform}/channels`} icon={UserRoundCog} label="Каналы" />
              <NavItem to={`/${platform}/live`} icon={Radio} label="Онлайн" />
            </>
          )}
          <NavItem to="/profile" icon={UserRoundCog} label="Профиль" />
          {platformAllowed && subscription?.[platform]?.subscribed && (
            <>
              {PLATFORMS.find((p) => p.id === platform)?.hasFollow && (
                <NavItem to={`/${platform}/follow`} icon={UserRoundCog} label="Фолловинг" />
              )}
              <NavItem to={`/${platform}/stats`} icon={BarChart3} label="Статистика" />
            </>
          )}
          {isAdmin && <NavItem to="/admin" icon={UserRoundCog} label="Администрирование" />}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
